import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

import GradeSelection from "@/components/home/GradeSelection";
import { supabase } from "@/integrations/supabase/client";
import UserAvatar from "@/components/profile/UserAvatar";
import UserBadges from "@/components/profile/UserBadges";
import { BadgeType } from "@/components/profile/UserBadge";
import { Link } from "react-router-dom";
import { Calendar, BarChart3, Mail, BookOpen } from "lucide-react";
import Seo from "@/components/seo/Seo";

interface UserBadge {
  id: string;
  type: BadgeType;
  label: string;
  description: string;
}

interface KpiState {
  pendingInvitations: number;
  upcomingSessions: number;
  testsTaken: number;
}

const StudentDashboard = () => {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [kpis, setKpis] = useState<KpiState>({
    pendingInvitations: 0,
    upcomingSessions: 0,
    testsTaken: 0,
  });

  useEffect(() => {
    if (!user) return;

    const loadProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name")
        .eq("id", user.id)
        .single();
      if (data) setFirstName(data.first_name || "");
    };

    const loadKpis = async () => {
      if (!user.email) return;
      const [invites, sessions, tests] = await Promise.all([
        supabase
          .from("test_invitations")
          .select("id", { count: "exact", head: true })
          .eq("email", user.email)
          .is("completed_at", null),
        supabase
          .from("calendar_events")
          .select("id", { count: "exact", head: true })
          .eq("student_user_id", user.id)
          .gte("start_time", new Date().toISOString()),
        supabase
          .from("test_sessions")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
      ]);
      setKpis({
        pendingInvitations: invites.count ?? 0,
        upcomingSessions: sessions.count ?? 0,
        testsTaken: tests.count ?? 0,
      });
    };

    loadProfile();
    loadKpis();

    // Static showcase badges (real badge system TBD)
    setBadges([
      { id: "1", type: "gold", label: "Perfect Score", description: "100% on a test" },
      { id: "2", type: "silver", label: "Quick Learner", description: "5 tests in a week" },
      { id: "3", type: "bronze", label: "First Test", description: "Completed your first test" },
    ]);
  }, [user]);

  const kpiTiles = [
    {
      label: "Pending invitations",
      value: kpis.pendingInvitations,
      icon: Mail,
      to: "/student/invitations",
    },
    {
      label: "Upcoming sessions",
      value: kpis.upcomingSessions,
      icon: Calendar,
      to: "/student/calendar",
    },
    {
      label: "Tests taken",
      value: kpis.testsTaken,
      icon: BarChart3,
      to: "/analysis",
    },
  ];

  return (
      <div className="container mx-auto px-4 py-8">
        <Seo title="Student Dashboard" description="Your personal learning dashboard." canonicalPath="/student" noindex />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-6"
        >
          {/* Welcome */}
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <UserAvatar userId={user?.id || ""} size="lg" editable />
                <div>
                  <CardTitle className="text-3xl font-bold">
                    Welcome, {firstName || user?.email?.split("@")[0]}!
                  </CardTitle>
                  <CardDescription>
                    Pick up where you left off and keep building your skills.
                  </CardDescription>
                  <div className="mt-2">
                    <UserBadges badges={badges} />
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* KPI tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {kpiTiles.map((tile) => (
              <Link key={tile.label} to={tile.to}>
                <Card className="hover:border-primary transition-colors h-full">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="p-3 rounded-md bg-muted">
                      <tile.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{tile.value}</div>
                      <div className="text-sm text-muted-foreground">{tile.label}</div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Start practicing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Start practicing
              </CardTitle>
              <CardDescription>Choose your grade to begin a practice test.</CardDescription>
            </CardHeader>
            <CardContent>
              <GradeSelection />
            </CardContent>
          </Card>

          {/* Quick links */}
          <Card>
            <CardHeader>
              <CardTitle>Quick links</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Button asChild variant="outline" className="h-auto py-3 justify-start">
                <Link to="/student/calendar" className="flex items-center gap-3">
                  <Calendar className="h-4 w-4" />
                  My calendar
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-auto py-3 justify-start">
                <Link to="/student/invitations" className="flex items-center gap-3">
                  <Mail className="h-4 w-4" />
                  Invitations
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-auto py-3 justify-start">
                <Link to="/analysis" className="flex items-center gap-3">
                  <BarChart3 className="h-4 w-4" />
                  Performance analysis
                </Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
  );
};

export default StudentDashboard;
